import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

export enum MarketingForgeElementType {
  RICH_TEXT = "rich-text",
  BUTTON = "button",
  INPUT = "input",
  IMAGE = "image",
  COLUMN = "column",
  DISCOUNT = "discount",
  TEXT_TO_SUBSCRIBE_BUTTON = "text-to-subscribe-button",
  TEXT_TO_SUBSCRIBE_DESCRIPTION = "text-to-subscribe-description",
}

export enum ButtonAction {
  SUBMIT_AND_ADVANCE = "submit-and-advance",
  CLOSE = "close",
}

export enum CustomFieldType {
  Date = "date",
  Dropdown = "dropdown",
  Number = "number",
  TextLong = "text-long",
  TextShort = "text-short",
  YesNoQuestion = "yes-no-question",
}

export const fontWeights = ["regular", "semibold", "bold"] as const;
export type FontWeight = (typeof fontWeights)[number];

export const buttonHierarchies = ["primary", "secondary", "tertiary"] as const;
export type ButtonHierarchy = (typeof buttonHierarchies)[number];

export const inputTypes = ["email", "phone", "birthday", "custom"] as const;
export type InputType = (typeof inputTypes)[number];

export enum FontFamily {
  ARIAL = "Arial",
  COURIER_NEW = "Courier New",
  GEORGIA = "Georgia",
  LUCIDA_SANS_UNICODE = "Lucida Sans Unicode",
  TAHOMA = "Tahoma",
  TIMES_NEW_ROMAN = "Times New Roman",
  TREBUCHET_MS = "Trebuchet MS",
  VERDANA = "Verdana",
}

export const richTextElementSchema = z.object({
  id: z.number(),
  type: z.literal(MarketingForgeElementType.RICH_TEXT),
  html: z.string(),
  fontFamily: z.string(),
  fontWeight: createEnum(fontWeights, { enumName: "FontWeight" }),
  fontSizePx: z.number(),
  textColor: z.string(),
  linkColor: z.string(),
});
export const RichTextElementSchema: z.ZodType<RichTextElement> =
  richTextElementSchema;
export type RichTextElement = z.infer<typeof richTextElementSchema>;

const buttonElementSchema = z.object({
  type: z.literal(MarketingForgeElementType.BUTTON),
  hierarchy: createEnum(buttonHierarchies, { enumName: "ButtonHierarchy" }),
  text: z.string(),
  action: createEnum(ButtonAction, { enumName: "ButtonAction" }),
});
export const ButtonElementSchema: z.ZodType<ButtonElement> =
  buttonElementSchema;
export type ButtonElement = z.infer<typeof buttonElementSchema>;

const inputElementSchema = z.object({
  type: z.literal(MarketingForgeElementType.INPUT),
  inputType: createEnum(inputTypes, { enumName: "InputTypes" }),
  placeholderText: z.string(),
  customField: z
    .object({
      id: z.string(),
      fieldType: createEnum(CustomFieldType, { enumName: "CustomFieldType" }),
    })
    .optional(),
  optional: z.boolean().optional(),
});
export const InputElementSchema: z.ZodType<InputElement> = inputElementSchema;
export type InputElement = z.infer<typeof inputElementSchema>;

export const imageElementSchema = z.object({
  type: z.literal(MarketingForgeElementType.IMAGE),
  url: z.string(),
});
export const ImageElementSchema: z.ZodType<ImageElement> = imageElementSchema;
export type ImageElement = z.infer<typeof imageElementSchema>;

export enum Alignment {
  LEFT = "left",
  CENTER = "center",
  RIGHT = "right",
}

export const alignmentToFlexAlign: Record<Alignment, string> = {
  [Alignment.LEFT]: "flex-start",
  [Alignment.CENTER]: "center",
  [Alignment.RIGHT]: "flex-end",
};

const discountCodeElementSchema = z.object({
  type: z.literal(MarketingForgeElementType.DISCOUNT),
  discountId: z.string().optional(),
  fontFamily: z.string(),
  fontWeight: createEnum(fontWeights, { enumName: "FontWeight" }),
  fontSize: z.number(),
  textColor: z.string(),
  blockBackgroundColor: z.string(),
  sectionColor: z.string(),
  sectionPadding: z.object({
    top: z.number(),
    right: z.number(),
    bottom: z.number(),
    left: z.number(),
  }),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  borderRadius: z.number(),
});
export const DiscountCodeElementSchema: z.ZodType<DiscountCodeElement> =
  discountCodeElementSchema;
export type DiscountCodeElement = z.infer<typeof discountCodeElementSchema>;

const textToSubscribeButtonElementSchema = buttonElementSchema.extend({
  type: z.literal(MarketingForgeElementType.TEXT_TO_SUBSCRIBE_BUTTON),
});
export const TextToSubscribeButtonElementSchema: z.ZodType<TextToSubscribeButtonElement> =
  textToSubscribeButtonElementSchema;
export type TextToSubscribeButtonElement = z.infer<
  typeof textToSubscribeButtonElementSchema
>;

const textToSubscribeDescriptionElementSchema = z.object({
  type: z.literal(MarketingForgeElementType.TEXT_TO_SUBSCRIBE_DESCRIPTION),
  fontFamily: z.string(),
  textColor: z.string(),
});
export const TextToSubscribeDescriptionElementSchema: z.ZodType<TextToSubscribeDescriptionElement> =
  textToSubscribeDescriptionElementSchema;
export type TextToSubscribeDescriptionElement = z.infer<
  typeof textToSubscribeDescriptionElementSchema
>;

const ElementUnion = z.union([
  richTextElementSchema,
  buttonElementSchema,
  inputElementSchema,
  imageElementSchema,
  discountCodeElementSchema,
  textToSubscribeButtonElementSchema,
  textToSubscribeDescriptionElementSchema,
]);

const columnElementSchema = z.object({
  type: z.literal(MarketingForgeElementType.COLUMN),
  elements: z.array(z.lazy(() => ElementUnion)),
  totalColumns: z.number(),
  columnWidths: z.array(z.number()).optional(),
});
export const ColumnElementSchema: z.ZodType<ColumnElement> =
  columnElementSchema;
export type ColumnElement = z.infer<typeof columnElementSchema>;
